import type { RendererProps } from "../core/element-registry"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { Skeleton } from "../components/ui/skeleton"
import { cn } from "../lib/utils"

export function SkeletonRenderer({ element }: RendererProps) {
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: skeleton's variant names a size preset (sm/md/lg/square/button); the
  // data-variant surface preserves the raw token for external assertion while
  // the className logic below continues to look up sizeClasses.
  const atomDefaults = useAtomDefaults("skeleton")
  const resolvedVariantRaw = element.variant ?? atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const sizeClasses: Record<string, string> = {
    sm: "h-5 w-32",
    md: "h-8 w-3/4",
    lg: "h-20 w-full",
    square: "aspect-square w-full rounded-xl",
    button: "h-10 w-40",
  }

  // When className already has dimension classes (h-/w-/size-) and no explicit variant
  // was set, skip variant defaults to avoid conflicting Tailwind classes
  const hasExplicitVariant = element.variant != null && element.variant in sizeClasses
  const hasDimensionInClassName = element.className != null && /\b(h-|w-|size-)/.test(element.className)
  const classes = (hasDimensionInClassName && !hasExplicitVariant) ? "" : (sizeClasses[element.variant ?? "md"] ?? sizeClasses.md)

  return <Skeleton className={cn(classes, element.className)} data-testid={`skeleton-${element.id}`} data-atom-type="skeleton" data-variant={resolvedVariantRaw} data-size={resolvedSize} />
}
